"use client";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

/** ---------- Types ---------- */
type ProposalRow = {
  staff_id: string;
  service_id: string;
  customer_id: string;
  start_time: string;
  end_time?: string;
  reason?: string;
  staff_name?: string;
  service_name?: string;
  customer_name?: string;
};

type DryRunResponse = {
  mode: "dry_run";
  proposals: ProposalRow[];
  requireConfirmation?: boolean;
  error?: string;
};

type ApplyResponse = { mode: "apply"; created: any[]; error?: string };

type Props = {
  edgeUrl?: string;
  defaultBusinessId?: string;
};

/** ---------- Utils ---------- */
const fmtDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const addDaysISO = (iso: string, n: number) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return fmtDate(d);
};

function groupByDay(rows: ProposalRow[]) {
  return rows.reduce<Record<string, ProposalRow[]>>((acc, r) => {
    const key = r.start_time.slice(0, 10);
    (acc[key] ||= []).push(r);
    return acc;
  }, {});
}

/** Base URL חסין + fallback ל־Edge Function */
function resolveEdgeUrl(propUrl?: string) {
  if (propUrl && /^https?:\/\//i.test(propUrl)) return propUrl.replace(/\/+$/, "");
  const envUrl =
    (typeof process !== "undefined" && (process.env.NEXT_PUBLIC_SUPABASE_URL || process.env.SUPABASE_URL)) ||
    // @ts-ignore
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_SUPABASE_URL) ||
    "";
  const base = envUrl && envUrl !== "undefined" ? envUrl : "https://nkuqcyelxgyihrxyvitb.supabase.co";
  return `${base.replace(/\/+$/, "")}/functions/v1/mcp-agent`;
}

/** תגית קטנה יפה לצ'יפס */
function Chip({
  children,
  tone = "slate",
}: {
  children: React.ReactNode;
  tone?: "slate" | "indigo" | "emerald" | "purple";
}) {
  const tones: Record<string, string> = {
    slate: "bg-slate-100 text-slate-700",
    indigo: "bg-indigo-100 text-indigo-700",
    emerald: "bg-emerald-100 text-emerald-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${tones[tone]} whitespace-nowrap`}>
      {children}
    </span>
  );
}

/** פיזור שעות (ללא שינוי RPC) */
type Bucket = "morning" | "noon" | "evening" | "other";
const hourBucket = (d: Date): Bucket => {
  const h = d.getHours();
  if (h < 11) return "morning";     // עד 10:59
  if (h < 15) return "noon";        // 11:00-14:59
  if (h < 20) return "evening";     // 15:00-19:59
  return "other";
};

function diversifyByBuckets(rows: ProposalRow[], limit: number) {
  // שומרים על סדר לפי שעה
  const sorted = [...rows].sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
  const buckets: Record<Bucket, ProposalRow[]> = { morning: [], noon: [], evening: [], other: [] };
  for (const r of sorted) buckets[hourBucket(new Date(r.start_time))].push(r);

  const order: Bucket[] = ["morning", "noon", "evening", "other"];
  const picked: ProposalRow[] = [];
  let round = 0;
  while (picked.length < limit && (buckets.morning.length + buckets.noon.length + buckets.evening.length + buckets.other.length) > 0) {
    for (const b of order) {
      const item = buckets[b].shift();
      if (item) picked.push(item);
      if (picked.length >= limit) break;
    }
    round++;
    if (round > 20) break; // safe guard
  }
  return picked;
}

/** ---------- Component ---------- */
export default function AutofillScheduler({ edgeUrl, defaultBusinessId }: Props) {
  const DAYS_AHEAD = 7;

  /** שלב תצוגה */
  const [step, setStep] = useState<"setup" | "results">("setup");

  /** URL סופי */
  const finalEdgeUrl = useMemo(() => resolveEdgeUrl(edgeUrl), [edgeUrl]);

  /** businessId */
  const [businessId, setBusinessId] = useState(defaultBusinessId || "");
  useEffect(() => {
    const fromLS = typeof window !== "undefined" ? localStorage.getItem("businessId") : null;
    if (fromLS && !businessId) setBusinessId(fromLS);
    (async () => {
      if (businessId) return;
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id;
      if (uid) {
        const { data } = await supabase.from("users").select("business_id").eq("id", uid).single();
        if (data?.business_id) setBusinessId(data.business_id);
      }
    })();
  }, [businessId]);

  /** bearer */
  const [authToken, setAuthToken] = useState("");
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.access_token) setAuthToken(data.session.access_token);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthToken(session?.access_token || "");
    });
    return () => sub?.subscription?.unsubscribe();
  }, []);

  /** UI state */
  const [startDate, setStartDate] = useState(() => fmtDate(new Date()));
  const endDate = useMemo(() => addDaysISO(startDate, DAYS_AHEAD - 1), [startDate]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [needsConfirm, setNeedsConfirm] = useState(false);

  // גיוון ושליטה
  const [limitPerDay, setLimitPerDay] = useState<number>(3);
  const [diversify, setDiversify] = useState<boolean>(true);
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});

  const grouped = useMemo(() => groupByDay(proposals), [proposals]);

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      "x-mcp-channel": "autofill-ui",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }),
    [authToken]
  );

  const k = (r: ProposalRow) => `${r.staff_id}__${r.service_id}__${r.customer_id}__${r.start_time}`;
  const selectedCount = useMemo(
    () => proposals.reduce((sum, r) => sum + (selected[k(r)] ? 1 : 0), 0),
    [proposals, selected]
  );

  const safe = (val?: string, fallback = "") => (val && String(val).trim() ? String(val).trim() : fallback);

  /** פוטר דביק – מדידה לדפדוף נעים */
  const footerRef = useRef<HTMLDivElement>(null);
  const [padBottom, setPadBottom] = useState(96);
  useLayoutEffect(() => {
    const measure = () => {
      const h = footerRef.current?.offsetHeight ?? 0;
      setPadBottom(h + 16);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (footerRef.current) ro.observe(footerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  /** Helper: העשרת שמות */
  async function enrichNames(rows: ProposalRow[]) {
    if (!rows.length) return rows;
    const staffIds = Array.from(new Set(rows.map(r => r.staff_id).filter(Boolean)));
    const serviceIds = Array.from(new Set(rows.map(r => r.service_id).filter(Boolean)));
    const customerIds = Array.from(new Set(rows.map(r => r.customer_id).filter(Boolean)));

    const [staffRes, serviceRes, customerRes] = await Promise.all([
      staffIds.length ? supabase.from("users").select("id,name").in("id", staffIds).eq("business_id", businessId) : Promise.resolve({ data: [] } as any),
      serviceIds.length ? supabase.from("services").select("id,name,name_he").in("id", serviceIds).eq("business_id", businessId) : Promise.resolve({ data: [] } as any),
      customerIds.length ? supabase.from("customers").select("id,name").in("id", customerIds).eq("business_id", businessId) : Promise.resolve({ data: [] } as any),
    ]);

    const staffMap = Object.fromEntries((staffRes.data ?? []).map((r: any) => [r.id, r.name]));
    const serviceMap = Object.fromEntries((serviceRes.data ?? []).map((r: any) => [r.id, r.name_he || r.name]));
    const customerMap = Object.fromEntries((customerRes.data ?? []).map((r: any) => [r.id, r.name]));

    return rows.map(r => ({
      ...r,
      staff_name: r.staff_name || staffMap[r.staff_id],
      service_name: r.service_name || serviceMap[r.service_id],
      customer_name: r.customer_name || customerMap[r.customer_id],
    }));
  }

  /** פעולות */
  async function dryRun() {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setProposals([]);
      setSelected({});
      setNeedsConfirm(false);
      setExpandedDays({});

      const payload = { mode: "autofill", action: "dry_run", businessId, startDate, endDate };
      const res = await fetch(finalEdgeUrl, { method: "POST", headers, body: JSON.stringify(payload) });
      const json: DryRunResponse = await res.json();
      if (!res.ok || json?.error) throw new Error(json?.error || res.statusText);

      const withNames = await enrichNames(json.proposals || []);
      setProposals(withNames);
      setNeedsConfirm(!!json.requireConfirmation);
      setStep("results");
    } catch (e: any) {
      setError(e?.message || "כשל בהצגת הצעות");
    } finally {
      setLoading(false);
    }
  }

  async function applyDirect() {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const chosenRows = proposals.filter((r) => selected[k(r)]);
      if (chosenRows.length === 0) {
        setError("לא נבחרו תורים");
        return;
      }

      const payload = { mode: "autofill", action: "apply", businessId, chosenRows, batchId: null };
      const res = await fetch(finalEdgeUrl, { method: "POST", headers, body: JSON.stringify(payload) });
      const json: ApplyResponse = await res.json();
      if (!res.ok || json?.error) throw new Error(json?.error || res.statusText);

      setSuccess("השיבוץ נשמר בהצלחה");
      setProposals([]);
      setSelected({});
      setNeedsConfirm(false);
      setStep("setup");
    } catch (e: any) {
      setError(e?.message || "כשל בשיבוץ");
    } finally {
      setLoading(false);
    }
  }

  function toggleDay(day: string, on: boolean) {
    const rows = grouped[day] || [];
    setSelected((prev) => {
      const out = { ...prev };
      for (const r of rows) out[k(r)] = on;
      return out;
    });
  }

  function toggleAll(on: boolean) {
    setSelected((prev) => {
      const out = { ...prev };
      for (const r of proposals) out[k(r)] = on;
      return out;
    });
  }

  /** שורות לתצוגה לפי יום עם גיוון/הגבלה */
  const rowsForDay = (day: string) => {
    const rows = (grouped[day] || []).slice().sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
    if (expandedDays[day]) return rows; // הצג הכל
    if (!limitPerDay || limitPerDay <= 0) return rows;
    return diversify ? diversifyByBuckets(rows, limitPerDay) : rows.slice(0, limitPerDay);
  };

  /** ---------- UI ---------- */
  return (
    <div
      dir="rtl"
      className="w-full h-full flex flex-col"
      style={{ minHeight: 0, overscrollBehavior: "contain", maxHeight: 520 }}
    >
      {/* Header */}
      <div className="pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-800">מילוי יומן</h2>
          {step === "results" && (
            <button
              onClick={() => setStep("setup")}
              className="text-xs text-indigo-700 hover:underline px-2 py-1 rounded"
              title="חזרה לבחירת תאריך"
            >
              שנה תאריך
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {step === "setup" ? "בחר תאריך התחלה – נציג הצעות לשבוע הקרוב." : "אלו ההצעות לשבוע שביקשת. בחר/י מה לשבץ."}
        </p>
      </div>

      {/* Step 1 */}
      {step === "setup" && (
        <div className="mb-2">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-700">תאריך התחלה</span>
              <input
                type="date"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ minWidth: 160 }}
              />
            </label>

            <div className="text-xs text-gray-500 select-none mb-[6px]">טווח: שבוע קבוע (7 ימים)</div>

            <button
              onClick={dryRun}
              disabled={loading || !businessId}
              className="ml-auto h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold shadow hover:bg-indigo-700 disabled:opacity-60"
              aria-busy={loading}
            >
              {loading ? "טוען..." : "הצג הצעות"}
            </button>
          </div>
        </div>
      )}

      {/* הודעות */}
      {needsConfirm && step === "results" && (
        <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-800 text-sm">
          ייתכן שיידרש אישור לפעולות מסוימות. בדוק/י את ההצעות לפני השיבוץ.
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-700 text-sm" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-700 text-sm" role="status" aria-live="polite">
          {success}
        </div>
      )}

      {/* אזור הגדרות התצוגה+בחירה גלובלית (רק בתוצאות) */}
      {step === "results" && proposals.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="text-xs text-gray-700 flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1">
            הצג עד
            <select
              className="text-xs border rounded px-1 py-[2px]"
              value={limitPerDay}
              onChange={(e) => setLimitPerDay(parseInt(e.target.value, 10))}
            >
              <option value={1}>1</option>
              <option value={3}>3</option>
              <option value={5}>5</option>
            </select>
            הצעות לכל יום
          </label>
          <label className="text-xs text-gray-700 flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-1">
            <input type="checkbox" checked={diversify} onChange={(e) => setDiversify(e.target.checked)} />
            גוון שעות
          </label>
          <button
            className="ml-auto text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
            onClick={() => toggleAll(true)}
          >
            בחר הכל (כל הימים)
          </button>
          <button
            className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
            onClick={() => toggleAll(false)}
          >
            נקה הכל
          </button>
        </div>
      )}

      {/* רשימה נגללת */}
      <div className="mt-3 flex-1 overflow-y-auto" style={{ paddingBottom: padBottom, minHeight: 0, maxHeight: 360 }}>
        {loading && (
          <div className="flex justify-center items-center py-10">
            <div className="inline-block w-6 h-6 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin mr-2" />
            <span className="text-sm text-gray-600">מחפש הצעות…</span>
          </div>
        )}

        {!loading && step === "results" && proposals.length === 0 && (
          <div className="text-center text-gray-400 py-8 text-sm">אין הצעות בשבוע שביקשת.</div>
        )}

        {!loading && step === "setup" && (
          <div className="text-center text-gray-400 py-8 text-sm">בחר/י תאריך ולחצ/י “הצג הצעות”.</div>
        )}

        {step === "results" && proposals.length > 0 && (
          <div className="space-y-6">
            {Object.entries(grouped).map(([day]) => {
              const allRows = (grouped[day] || []).slice().sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));
              const shown = rowsForDay(day);
              const hasMore = shown.length < allRows.length;

              return (
                <section key={day} className="rounded-xl">
                  {/* כותרת יום */}
                  <div className="flex items-center justify-between py-2 px-1 sticky top-0 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 z-10 border-b border-gray-100">
                    <div className="font-semibold text-gray-700 text-sm">
                      {new Date(day).toLocaleDateString("he-IL", { weekday: "long", day: "2-digit", month: "2-digit" })}
                    </div>
                    <div className="flex items-center gap-3">
                      {hasMore ? (
                        <button
                          onClick={() => setExpandedDays((p) => ({ ...p, [day]: true }))}
                          className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
                        >
                          הצג הכל
                        </button>
                      ) : expandedDays[day] ? (
                        <button
                          onClick={() => setExpandedDays((p) => ({ ...p, [day]: false }))}
                          className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
                        >
                          הצג פחות
                        </button>
                      ) : null}
                      <button
                        onClick={() => toggleDay(day, true)}
                        className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
                      >
                        בחר הכל
                      </button>
                      <button
                        onClick={() => toggleDay(day, false)}
                        className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-100"
                      >
                        נקה
                      </button>
                    </div>
                  </div>

                  {/* שורות */}
                  <div className="divide-y divide-gray-100">
                    {shown.map((r, idx) => {
                      const key = k(r);
                      const checked = !!selected[key];
                      const t = new Date(r.start_time).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

                      const customer = safe(r.customer_name, "לקוח");
                      const staff = safe(r.staff_name, "איש צוות");
                      const service = safe(r.service_name, "שירות");

                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-3 px-1 sm:px-2 py-3 rounded-lg hover:bg-indigo-50/40 cursor-pointer ${
                            idx === shown.length - 1 ? "last:mb-2" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="w-5 h-5 accent-indigo-600"
                            checked={checked}
                            onChange={() => setSelected((p) => ({ ...p, [key]: !p[key] }))}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">{t}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <Chip tone="indigo">לקוח&nbsp;·&nbsp;{customer}</Chip>
                              <Chip tone="emerald">שירות&nbsp;·&nbsp;{service}</Chip>
                              <Chip tone="purple">איש&nbsp;צוות&nbsp;·&nbsp;{staff}</Chip>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* פאנל פעולות */}
      <div
        ref={footerRef}
        className="mt-3 pt-3 border-t border-gray-100 bg-white sticky bottom-0"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 6px)" }}
      >
        <div className="flex items-center gap-3">
          {step === "results" ? (
            <>
              <button
                onClick={applyDirect}
                disabled={selectedCount === 0 || loading}
                className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold shadow hover:bg-emerald-700 disabled:opacity-50"
              >
                שבץ {selectedCount > 0 ? `(${selectedCount})` : ""}
              </button>
              <span className="text-xs text-gray-500">בחר/י לפחות הצעה אחת</span>
              <button
                onClick={() => setStep("setup")}
                className="ml-auto h-10 px-3 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200"
              >
                חזור
              </button>
            </>
          ) : (
            <button
              onClick={dryRun}
              disabled={loading || !businessId}
              className="ml-auto h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold shadow hover:bg-indigo-700 disabled:opacity-60"
              aria-busy={loading}
            >
              {loading ? "טוען..." : "הצג הצעות"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
